import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  Target,
  Radio,
  Eye,
  Volume2,
  Smartphone,
  Clock,
  AlertOctagon,
} from "lucide-react";
import { apiJson } from "../lib/queryClient";
import type { ProximityAlarmSettingsT } from "@shared/schema";

const TA_COLOR = "#FF9500";
const RA_COLOR = "#FF3B30";

const DEFAULT_PROXIMITY_ALARM: ProximityAlarmSettingsT = {
  enabled: true,
  maxAltitudeM: 500,
  radiusKm: 5,
  approachRadiusM: 2000,
  ta: { radiusKm: 3, visual: true, audio: true, haptic: true },
  ra: { radiusKm: 1, visual: true, audio: true, haptic: true },
  tcasTimeEnabled: true,
  taTimeSec: 40,
  raTimeSec: 30,
  taTimeEnabled: true,
  raTimeEnabled: true,
};

type SettingsResponse = {
  proximityAlarm?: ProximityAlarmSettingsT | null;
};

function formatRadius(meters: number): string {
  if (meters >= 1000) {
    const km = meters / 1000;
    return km % 1 === 0 ? `${km} km` : `${km.toFixed(1)} km`;
  }
  return `${meters} m`;
}

function snapToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function mergeProximityAlarm(
  remote?: ProximityAlarmSettingsT | null,
): ProximityAlarmSettingsT {
  if (!remote) return DEFAULT_PROXIMITY_ALARM;
  return {
    ...DEFAULT_PROXIMITY_ALARM,
    ...remote,
    ta: { ...DEFAULT_PROXIMITY_ALARM.ta, ...(remote.ta || {}) },
    ra: { ...DEFAULT_PROXIMITY_ALARM.ra, ...(remote.ra || {}) },
    taTimeEnabled: remote.taTimeEnabled ?? remote.tcasTimeEnabled ?? true,
    raTimeEnabled: remote.raTimeEnabled ?? remote.tcasTimeEnabled ?? true,
  };
}

export default function TcasSettings() {
  const qc = useQueryClient();
  const { data } = useQuery<SettingsResponse | null>({
    queryKey: ["/api/settings"],
  });
  const [pa, setPa] = useState<ProximityAlarmSettingsT>(DEFAULT_PROXIMITY_ALARM);

  useEffect(() => {
    if (data) setPa(mergeProximityAlarm(data.proximityAlarm));
  }, [data]);

  const save = useMutation({
    mutationFn: (next: ProximityAlarmSettingsT) =>
      apiJson("POST", "/api/settings", { proximityAlarm: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/settings"] });
    },
  });

  function commit(next: ProximityAlarmSettingsT) {
    setPa(next);
    save.mutate(next);
  }

  return (
    <div className="space-y-3 max-w-2xl mx-auto pb-6">
      {/* Web-Hinweis: optische/akustische/Vibrations-Alarme nur in der App */}
      <div className="card bg-[var(--color-caution-bg)] border border-[var(--color-caution)]">
        <p className="text-xs text-[var(--color-caution)] leading-relaxed">
          Hinweis: Die Live-Verkehrserkennung (TCAS) mit akustischen und
          Vibrations-Alarmen läuft ausschließlich in der mobilen App. Hier in der
          Web-App kannst du die Einstellungen vorab konfigurieren – sie werden
          mit deinem Konto synchronisiert.
        </p>
      </div>

      {/* TCAS aktiviert */}
      <div className="card flex items-center gap-3">
        <AlertTriangle size={20} className="text-brand-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[var(--color-app-text)]">TCAS aktiviert</div>
          <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">
            Traffic Alert and Collision Avoidance
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={pa.enabled}
          onClick={() => commit({ ...pa, enabled: !pa.enabled })}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
            pa.enabled ? "bg-brand-600" : "bg-[var(--color-app-bg-tertiary)]"
          }`}
          data-testid="toggle-proximity-alarm"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              pa.enabled ? "translate-x-5" : ""
            }`}
          />
        </button>
      </div>

      {/* Max. Höhe (AGL) */}
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <ArrowDown size={16} className="text-brand-600" />
          <span className="text-sm text-[var(--color-app-text)]">Max. Höhe (AGL)</span>
          <span className="ml-auto text-sm font-bold text-brand-600">
            {pa.maxAltitudeM} m
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[var(--color-app-text-tertiary)] w-7 text-center">0m</span>
          <input
            type="range"
            min={0}
            max={500}
            step={10}
            value={pa.maxAltitudeM}
            onChange={(e) =>
              setPa({ ...pa, maxAltitudeM: snapToStep(+e.target.value, 10) })
            }
            onPointerUp={(e) =>
              commit({
                ...pa,
                maxAltitudeM: snapToStep(
                  +(e.target as HTMLInputElement).value,
                  10,
                ),
              })
            }
            className="flex-1 accent-brand-600"
            data-testid="slider-max-altitude"
          />
          <span className="text-[10px] text-[var(--color-app-text-tertiary)] w-7 text-center">
            500m
          </span>
        </div>
      </div>

      {/* Erfassung = Radar-Radius */}
      <div className="card flex items-center gap-2">
        <Target size={18} className="text-brand-600" />
        <span className="text-sm text-[var(--color-app-text)]">Erfassung</span>
        <span className="text-xs text-[var(--color-app-text-tertiary)]">= Radar-Radius</span>
      </div>

      {/* TA - Traffic Advisory */}
      <div className="pt-2">
        <h2
          className="text-sm font-bold ml-1"
          style={{ color: TA_COLOR }}
        >
          TA - Traffic Advisory (Gelb)
        </h2>
        <p className="text-[11px] text-[var(--color-app-text-tertiary)] ml-1 mb-2">
          Warnung vor nahem Verkehr
        </p>
      </div>

      {/* TA Radius */}
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Radio size={16} style={{ color: TA_COLOR }} />
          <span className="text-sm text-[var(--color-app-text)]">Radius</span>
          <span className="ml-auto text-sm font-bold" style={{ color: TA_COLOR }}>
            {formatRadius(pa.ta.radiusKm * 1000)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[var(--color-app-text-tertiary)] w-7 text-center">50m</span>
          <input
            type="range"
            min={50}
            max={5000}
            step={50}
            value={pa.ta.radiusKm * 1000}
            onChange={(e) =>
              setPa({
                ...pa,
                ta: { ...pa.ta, radiusKm: snapToStep(+e.target.value, 50) / 1000 },
              })
            }
            onPointerUp={(e) =>
              commit({
                ...pa,
                ta: {
                  ...pa.ta,
                  radiusKm:
                    snapToStep(+(e.target as HTMLInputElement).value, 50) / 1000,
                },
              })
            }
            className="flex-1"
            style={{ accentColor: TA_COLOR }}
            data-testid="slider-ta-radius"
          />
          <span className="text-[10px] text-[var(--color-app-text-tertiary)] w-7 text-center">5km</span>
        </div>
      </div>

      {/* TA Toggles */}
      <div className="card">
        <div className="flex gap-2">
          <TcasToggle
            icon={<Eye size={14} />}
            label="Optisch"
            active={pa.ta.visual}
            color={TA_COLOR}
            onToggle={() =>
              commit({ ...pa, ta: { ...pa.ta, visual: !pa.ta.visual } })
            }
            testId="toggle-ta-visual"
          />
          <TcasToggle
            icon={<Volume2 size={14} />}
            label="Akustisch"
            active={pa.ta.audio}
            color={TA_COLOR}
            onToggle={() =>
              commit({ ...pa, ta: { ...pa.ta, audio: !pa.ta.audio } })
            }
            testId="toggle-ta-audio"
          />
          <TcasToggle
            icon={<Smartphone size={14} />}
            label="Vibration"
            active={pa.ta.haptic}
            color={TA_COLOR}
            onToggle={() =>
              commit({ ...pa, ta: { ...pa.ta, haptic: !pa.ta.haptic } })
            }
            testId="toggle-ta-haptic"
          />
        </div>
      </div>

      {/* TA Zeit */}
      <div className="card flex flex-wrap items-center gap-2">
        <Clock size={16} style={{ color: TA_COLOR }} />
        <span className="text-sm text-[var(--color-app-text)]">TA Zeit</span>
        <div className="flex gap-1.5 ml-auto">
          <TimeChip
            label="Aus"
            active={!(pa.taTimeEnabled ?? true)}
            color={TA_COLOR}
            onClick={() =>
              commit({ ...pa, taTimeEnabled: !(pa.taTimeEnabled ?? true) })
            }
            testId="chip-ta-off"
          />
          {[30, 40, 45, 60].map((val) => (
            <TimeChip
              key={val}
              label={`${val}s`}
              active={(pa.taTimeEnabled ?? true) && pa.taTimeSec === val}
              color={TA_COLOR}
              onClick={() =>
                commit({ ...pa, taTimeEnabled: true, taTimeSec: val })
              }
              testId={`chip-ta-${val}`}
            />
          ))}
        </div>
      </div>

      {/* RA - Resolution Advisory */}
      <div className="pt-2">
        <h2 className="text-sm font-bold ml-1" style={{ color: RA_COLOR }}>
          RA - Resolution Advisory (Rot)
        </h2>
        <p className="text-[11px] text-[var(--color-app-text-tertiary)] ml-1 mb-2">
          Kritische Annäherung - Ausweichanweisung
        </p>
      </div>

      {/* RA Radius */}
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <AlertOctagon size={16} style={{ color: RA_COLOR }} />
          <span className="text-sm text-[var(--color-app-text)]">Radius</span>
          <span className="ml-auto text-sm font-bold" style={{ color: RA_COLOR }}>
            {formatRadius(pa.ra.radiusKm * 1000)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[var(--color-app-text-tertiary)] w-7 text-center">50m</span>
          <input
            type="range"
            min={50}
            max={2000}
            step={50}
            value={pa.ra.radiusKm * 1000}
            onChange={(e) =>
              setPa({
                ...pa,
                ra: { ...pa.ra, radiusKm: snapToStep(+e.target.value, 50) / 1000 },
              })
            }
            onPointerUp={(e) =>
              commit({
                ...pa,
                ra: {
                  ...pa.ra,
                  radiusKm:
                    snapToStep(+(e.target as HTMLInputElement).value, 50) / 1000,
                },
              })
            }
            className="flex-1"
            style={{ accentColor: RA_COLOR }}
            data-testid="slider-ra-radius"
          />
          <span className="text-[10px] text-[var(--color-app-text-tertiary)] w-7 text-center">2km</span>
        </div>
      </div>

      {/* RA Toggles */}
      <div className="card">
        <div className="flex gap-2">
          <TcasToggle
            icon={<Eye size={14} />}
            label="Optisch"
            active={pa.ra.visual}
            color={RA_COLOR}
            onToggle={() =>
              commit({ ...pa, ra: { ...pa.ra, visual: !pa.ra.visual } })
            }
            testId="toggle-ra-visual"
          />
          <TcasToggle
            icon={<Volume2 size={14} />}
            label="Akustisch"
            active={pa.ra.audio}
            color={RA_COLOR}
            onToggle={() =>
              commit({ ...pa, ra: { ...pa.ra, audio: !pa.ra.audio } })
            }
            testId="toggle-ra-audio"
          />
          <TcasToggle
            icon={<Smartphone size={14} />}
            label="Vibration"
            active={pa.ra.haptic}
            color={RA_COLOR}
            onToggle={() =>
              commit({ ...pa, ra: { ...pa.ra, haptic: !pa.ra.haptic } })
            }
            testId="toggle-ra-haptic"
          />
        </div>
      </div>

      {/* RA Zeit */}
      <div className="card flex flex-wrap items-center gap-2">
        <Clock size={16} style={{ color: RA_COLOR }} />
        <span className="text-sm text-[var(--color-app-text)]">RA Zeit</span>
        <div className="flex gap-1.5 ml-auto">
          <TimeChip
            label="Aus"
            active={!(pa.raTimeEnabled ?? true)}
            color={RA_COLOR}
            onClick={() =>
              commit({ ...pa, raTimeEnabled: !(pa.raTimeEnabled ?? true) })
            }
            testId="chip-ra-off"
          />
          {[15, 20, 25, 30].map((val) => (
            <TimeChip
              key={val}
              label={`${val}s`}
              active={(pa.raTimeEnabled ?? true) && pa.raTimeSec === val}
              color={RA_COLOR}
              onClick={() =>
                commit({ ...pa, raTimeEnabled: true, raTimeSec: val })
              }
              testId={`chip-ra-${val}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TcasToggle({
  icon,
  label,
  active,
  color,
  onToggle,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  color: string;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-semibold transition"
      style={
        active
          ? { backgroundColor: color, color: "#FFFFFF" }
          : { backgroundColor: "#F1F5F9", color: "#94A3B8" }
      }
      data-testid={testId}
    >
      {icon}
      {label}
    </button>
  );
}

function TimeChip({
  label,
  active,
  color,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  color: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1 rounded-md text-xs font-semibold transition"
      style={
        active
          ? { backgroundColor: color, color: "#FFFFFF" }
          : { backgroundColor: "#F1F5F9", color: "#475569" }
      }
      data-testid={testId}
    >
      {label}
    </button>
  );
}
